class StoreGrid {
  constructor(sectionId, config) {
    this.sectionId = sectionId;
    this.config = config;
    this.allStores = [];
    this.filteredStores = [];
    this.selectedProvinces = [];
    this.selectedBrands = [];
    this.searchTerm = "";
    this.preloader = document.getElementById(`preloader-${sectionId}`);
    this.wrapper = document.querySelector(
      `#store-grid-${sectionId} .store-grid-wrapper`
    );

    this.init();
  }

  // Helper method to identify store type
  getStoreType(store) {
    const brand = store.brand || "";
    const name = store.name || "";

    // Prioritize brand field over name field for accurate classification
    if (brand === "Osmo") {
      return "osmo";
    } else if (brand === "Refinery Junior") {
      return "junior";
    } else if (brand === "Refinery") {
      return "refinery";
    }

    // Fallback to name checking if brand field is not set or doesn't match
    // Use word boundary regex to match "osmo" as a whole word, not part of another word (e.g., "Cosmo")
    if (/\bosmo\b/i.test(name)) {
      return "osmo";
    } else if (name.includes("Junior")) {
      return "junior";
    }

    // Default to refinery for backward compatibility
    return "refinery";
  }

  formatStore(store) {
    return {
      ...store,
      id:
        store.id ||
        store._id ||
        `manual_${Math.random().toString(36).substr(2, 9)}`,
      latitude: parseFloat(store.latitude),
      longitude: parseFloat(store.longitude),
      operating_hours: store.operating_hours || store.operatingHours || {},
      storeType: this.getStoreType(store),
    };
  }

  formatOperatingHours(hours) {
    if (!hours || Object.keys(hours).length === 0) return "Hours not available";

    const daysOrder = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];

    const formatTime = (timeStr) => {
      if (!timeStr) return "";
      return timeStr.includes(":") ? timeStr.slice(0, 5) : "";
    };

    // Group consecutive days with same hours
    const groupedHours = [];
    let currentGroup = null;

    daysOrder.forEach((day) => {
      const dayHours = hours[day];
      if (!dayHours || !dayHours.open || !dayHours.close) {
        if (currentGroup) {
          groupedHours.push(currentGroup);
          currentGroup = null;
        }
        return;
      }

      const openTime = formatTime(dayHours.open);
      const closeTime = formatTime(dayHours.close);
      const timeRange = `${openTime}-${closeTime}`;

      if (!currentGroup) {
        currentGroup = {
          days: [day],
          timeRange: timeRange,
        };
      } else if (currentGroup.timeRange === timeRange) {
        currentGroup.days.push(day);
      } else {
        groupedHours.push(currentGroup);
        currentGroup = {
          days: [day],
          timeRange: timeRange,
        };
      }
    });

    if (currentGroup) {
      groupedHours.push(currentGroup);
    }

    const formatDayRange = (days) => {
      if (days.length === 1) {
        return days[0].charAt(0).toUpperCase() + days[0].slice(1, 3);
      }
      const firstDay = days[0].charAt(0).toUpperCase() + days[0].slice(1, 3);
      const lastDay =
        days[days.length - 1].charAt(0).toUpperCase() +
        days[days.length - 1].slice(1, 3);
      return `${firstDay}-${lastDay}`;
    };

    let hoursContent = "";
    groupedHours.forEach((group) => {
      if (group.days.length > 0) {
        hoursContent += `${formatDayRange(group.days)}: ${group.timeRange}; `;
      }
    });

    return hoursContent.trim() || "Hours not available";
  }

  async init() {
    try {
      await this.loadStores();
      this.setupEventListeners();
      this.hideLoader();
    } catch (error) {
      console.error("Initialization failed:", error);
      this.showError("Failed to load stores. Please try again later.");
      this.hideLoader();
    }
  }

  hideLoader() {
    if (this.preloader) {
      this.preloader.style.opacity = "0";
      if (this.wrapper) {
        this.wrapper.style.opacity = "1";
        this.wrapper.style.visibility = "visible";
      }
      setTimeout(() => {
        document.body.classList.add("grid-loaded");
        this.preloader.style.display = "none";
      }, 300);
    }
  }

  async loadStores() {
    try {
      const response = await fetch(
        `${this.config.backendUrl}/api/stores?brand=${this.config.brand}`
      );
      const dbStores = response.ok ? await response.json() : [];

      // Filter out generic stores with specific keywords_identifier values
      const excludedKeywords = [
        "gracious-impertinent-limpkin",
        "depictive-poorspirited-needletail",
      ];
      const filteredDbStores = dbStores.filter(
        (store) => !excludedKeywords.includes(store.keywords_identifier)
      );

      this.allStores = filteredDbStores.map((store) => this.formatStore(store));

      // Sort alphabetically by default
      this.allStores.sort((a, b) => {
        const nameA = (a.name || "").toLowerCase();
        const nameB = (b.name || "").toLowerCase();
        return nameA.localeCompare(nameB);
      });

      this.filteredStores = [...this.allStores];
      this.displayStores();
      this.updateStats();
    } catch (error) {
      console.error("Failed to fetch stores:", error);
      throw error;
    }
  }

  setupEventListeners() {
    // Search input
    const searchInput = document.getElementById(
      `store-search-${this.sectionId}`
    );
    const clearButton = document.querySelector(
      `#store-search-${this.sectionId} + .store-grid-search-clear`
    );

    if (searchInput) {
      searchInput.addEventListener("input", (e) => {
        this.searchTerm = e.target.value.toLowerCase().trim();
        this.applyFilters();
      });
    }

    if (clearButton) {
      clearButton.addEventListener("click", () => {
        this.searchTerm = "";
        if (searchInput) searchInput.value = "";
        this.applyFilters();
      });
    }

    // Province filter toggle
    const provinceToggle = document.querySelector(
      `.province-filter-toggle[data-section-id="${this.sectionId}"]`
    );
    if (provinceToggle) {
      provinceToggle.addEventListener("click", () => {
        const options = provinceToggle.nextElementSibling;
        const isExpanded =
          provinceToggle.getAttribute("aria-expanded") === "true";
        provinceToggle.setAttribute("aria-expanded", !isExpanded);
        options.setAttribute("aria-hidden", isExpanded);
        options.classList.toggle("visible", !isExpanded);
      });
    }

    // Province checkboxes
    const provinceCheckboxes = document.querySelectorAll(
      `.province-checkbox[data-section-id="${this.sectionId}"]`
    );
    provinceCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", () => {
        this.selectedProvinces = Array.from(
          document.querySelectorAll(
            `.province-checkbox[data-section-id="${this.sectionId}"]:checked`
          )
        ).map((cb) => cb.value);
        this.applyFilters();
      });
    });

    // Brand filter toggle
    const brandToggle = document.querySelector(
      `.brand-filter-toggle[data-section-id="${this.sectionId}"]`
    );
    if (brandToggle) {
      brandToggle.addEventListener("click", () => {
        const options = brandToggle.nextElementSibling;
        const isExpanded = brandToggle.getAttribute("aria-expanded") === "true";
        brandToggle.setAttribute("aria-expanded", !isExpanded);
        options.setAttribute("aria-hidden", isExpanded);
        options.classList.toggle("visible", !isExpanded);
      });
    }

    // Brand checkboxes
    const brandCheckboxes = document.querySelectorAll(
      `.brand-checkbox[data-section-id="${this.sectionId}"]`
    );
    brandCheckboxes.forEach((box) => {
      box.addEventListener("change", (e) => {
        const value = e.target.value;

        // Handle "All Stores" checkbox
        if (value === "") {
          if (e.target.checked) {
            brandCheckboxes.forEach((b) => {
              if (b !== e.target) b.checked = false;
            });
            this.selectedBrands = [];
          }
        } else {
          // Handle individual brand checkboxes
          if (e.target.checked) {
            const allStoresBox = document.getElementById(
              `brand-all-${this.sectionId}`
            );
            if (allStoresBox) allStoresBox.checked = false;
            if (!this.selectedBrands.includes(value)) {
              this.selectedBrands.push(value);
            }
          } else {
            this.selectedBrands = this.selectedBrands.filter(
              (b) => b !== value
            );
            if (this.selectedBrands.length === 0) {
              const allStoresBox = document.getElementById(
                `brand-all-${this.sectionId}`
              );
              if (allStoresBox) allStoresBox.checked = true;
            }
          }
        }

        this.applyFilters();
      });
    });
  }

  applyFilters() {
    let filtered = [...this.allStores];

    // Apply province filter
    if (this.selectedProvinces.length > 0) {
      const countryLevelFilters = [
        "Namibia",
        "Lesotho",
        "Eswatini",
        "Botswana",
      ];
      const selectedCountries = this.selectedProvinces.filter((selection) =>
        countryLevelFilters.includes(selection)
      );
      const selectedSaProvinces = this.selectedProvinces.filter(
        (selection) => !countryLevelFilters.includes(selection)
      );

      filtered = filtered.filter((store) => {
        if (selectedCountries.includes(store.country_name)) {
          return true;
        }
        if (selectedSaProvinces.includes(store.province)) {
          return true;
        }
        return false;
      });
    }

    // Apply brand filter
    if (this.selectedBrands.length > 0) {
      filtered = filtered.filter((store) =>
        this.selectedBrands.includes(store.storeType)
      );
    }

    // Apply search filter
    if (this.searchTerm) {
      filtered = filtered.filter((store) => {
        const searchFields = [
          store.name,
          store.address,
          store.contact_number,
          store.province,
          store.country_name,
        ]
          .join(" ")
          .toLowerCase();
        return searchFields.includes(this.searchTerm);
      });
    }

    // Sort alphabetically
    filtered.sort((a, b) => {
      const nameA = (a.name || "").toLowerCase();
      const nameB = (b.name || "").toLowerCase();
      return nameA.localeCompare(nameB);
    });

    this.filteredStores = filtered;
    this.displayStores();
    this.updateStats();
  }

  displayStores() {
    const container = document.getElementById(
      `store-grid-container-${this.sectionId}`
    );
    const emptyMessage = document.getElementById(
      `store-grid-empty-${this.sectionId}`
    );

    if (!container) return;

    if (this.filteredStores.length === 0) {
      container.innerHTML = "";
      if (emptyMessage) emptyMessage.style.display = "block";
      return;
    }

    if (emptyMessage) emptyMessage.style.display = "none";

    container.innerHTML = this.filteredStores
      .map((store) => this.createStoreCard(store))
      .join("");

    // Add click handlers for Google Maps links
    container.querySelectorAll(".store-google-maps-link").forEach((link) => {
      link.addEventListener("click", (e) => {
        e.preventDefault();
        window.open(link.href, "_blank");
      });
    });
  }

  createStoreCard(store) {
    const operatingHours = this.formatOperatingHours(store.operating_hours);
    const brandLabel =
      store.storeType === "osmo"
        ? "Osmo"
        : store.storeType === "junior"
        ? "Refinery Junior"
        : "Refinery";

    return `
      <div class="store-card" data-store-id="${store.id}">
        <div class="store-card-header">
          <h3 class="store-card-name">${this.escapeHtml(store.name)}</h3>
          <span class="store-card-brand">${brandLabel}</span>
        </div>
        <div class="store-card-body">
          <div class="store-card-detail">
            <strong>Address:</strong>
            <p>${this.escapeHtml(store.address || "N/A")}</p>
          </div>
          <div class="store-card-detail">
            <strong>Contact:</strong>
            <p>${this.escapeHtml(store.contact_number || "N/A")}</p>
          </div>
          <div class="store-card-detail">
            <strong>Province/Region:</strong>
            <p>${this.escapeHtml(store.province || "N/A")}</p>
          </div>
          ${
            store.country_name
              ? `
          <div class="store-card-detail">
            <strong>Country:</strong>
            <p>${this.escapeHtml(store.country_name)}</p>
          </div>
          `
              : ""
          }
          <div class="store-card-detail">
            <strong>Operating Hours:</strong>
            <p>${this.escapeHtml(operatingHours)}</p>
          </div>
          ${
            store.status
              ? `
          <div class="store-card-detail">
            <strong>Status:</strong>
            <p class="store-status ${store.status
              .toLowerCase()
              .replace(/\s+/g, "-")}">${this.escapeHtml(store.status)}</p>
          </div>
          `
              : ""
          }
        </div>
        <div class="store-card-footer">
          ${
            store.google_maps_url
              ? `
          <a href="${store.google_maps_url}" 
             target="_blank" 
             class="store-google-maps-link"
             style="background-color: ${this.config.buttonColor}; color: ${this.config.buttonTextColor};">
            View on Google Maps
          </a>
          `
              : ""
          }
        </div>
      </div>
    `;
  }

  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  updateStats() {
    const statsElement = document.getElementById(
      `store-stats-${this.sectionId}`
    );
    if (statsElement) {
      const total = this.allStores.length;
      const showing = this.filteredStores.length;
      statsElement.innerHTML = `<p>Showing ${showing} of ${total} stores</p>`;
    }
  }

  showError(message) {
    const container = document.getElementById(
      `store-grid-container-${this.sectionId}`
    );
    if (container) {
      container.innerHTML = `<div class="store-grid-error"><p>${message}</p></div>`;
    }
  }
}

document.addEventListener("DOMContentLoaded", () => {
  Object.keys(window.storeGridConfig).forEach((sectionId) => {
    try {
      new StoreGrid(sectionId, window.storeGridConfig[sectionId]);
    } catch (error) {
      console.error(
        `Failed to initialize store grid for section ${sectionId}:`,
        error
      );
    }
  });
});
